import { faClock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { ConversationListItem } from "api/services/messaging/schemas/messaging";
import { Avatar } from "shared/atoms/Avatar";
import { RelativeDatetime } from "shared/atoms/RelativeDatetime";
import { BLUE } from "theme/colors";

import { CustomNavLink } from "../styles";

interface ConversationItemProps
  extends Pick<ConversationListItem, "has_unread_messages" | "last_message" | "peer" | "pinned_messages"> {
  hasDraft?: boolean;
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  hasDraft = false,
  has_unread_messages,
  last_message,
  peer,
  pinned_messages,
}) => {
  const { t } = useTranslation();
  const peerName = `${peer.first_name || ""} ${peer.last_name || ""}`.trim();

  const getMessage = () => {
    if (pinned_messages.length > 0) {
      return pinned_messages[0].body;
    }
    if (last_message) {
      return last_message.body;
    }
    return "-";
  };

  return (
    <StyledNavLink to={`../patients/${peer.id}`} state={{ peerName }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Box sx={{ position: "relative" }}>
          <Avatar size={40} id={peer.id} name={peerName} src={peer.avatars.medium || undefined} />
          {peer.therapist_assignment_role === "substitute_therapist" && (
            <FontAwesomeIcon icon={faClock} color={BLUE[100]} style={{ position: "absolute", bottom: -4, right: -4 }} />
          )}
        </Box>

        <Stack spacing={1} sx={{ flex: 1, overflow: "hidden" }}>
          <Typography variant="subtitle1" noWrap>
            {peerName}
          </Typography>

          <Typography variant="label" noWrap>
            {getMessage()}
          </Typography>
        </Stack>

        <Stack spacing={1} sx={{ alignItems: "flex-end", alignSelf: "stretch" }}>
          <RelativeDatetime
            capitalize
            dateString={last_message?.created_at || ""}
            typographyVariant="label_small"
            shortFormat
          />
          {(hasDraft || has_unread_messages || pinned_messages.length > 0) && (
            <StatusIndicator
              data-testid="new-message-label"
              className={clsx({ draft: hasDraft, new: has_unread_messages || pinned_messages.length > 0 })}
            >
              <Typography variant="label">{hasDraft ? t("common.draft") : t("common.new")[1]}</Typography>
            </StatusIndicator>
          )}
        </Stack>
      </Stack>
    </StyledNavLink>
  );
};

const StyledNavLink = styled(CustomNavLink)`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db10};

  &:hover,
  &.active {
    background-color: ${({ theme }) => theme.colors.redesign.b10};
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background-color: ${({ theme }) => theme.colors.redesign.b10};
  border-radius: 4px;

  & > span {
    color: ${({ theme }) => theme.colors.redesign.b100};
  }

  &.draft {
    background-color: ${({ theme }) => theme.colors.redesign.o10};

    & > span {
      color: ${({ theme }) => theme.colors.redesign.o100};
    }
  }
`;
